import React, { FunctionComponent, MutableRefObject, useState } from 'react'
import { defineMessage, useIntl } from 'react-intl'
import { AccessibilityPropsAndroid, LayoutChangeEvent, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { backAccessibilityLabel } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'

import { Display, Size as DisplaySize } from '../../atoms/display/Display'
import { ArrowIcon, ArrowIconDirection } from '../../atoms/icons/ArrowIcon'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { AccessibilityRolesNative, IS_WEB, Layout } from '../../constants'
import { ProgressBar } from '../../molecules/progressBar/ProgressBar'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ActivityHeaderProps = {
  title?: string
  onBackPress?: () => void
  onClosePress?: () => void
  percentProgress?: number
  status?: string
  date?: string | null
  inReviewMode?: boolean
  backgroundColor?: string
  setHeaderHeight?: (height: number) => void
  a11yFocusedElementOnLoad?: MutableRefObject<any>
  progressBarAccessibilityLiveRegion?: AccessibilityPropsAndroid['accessibilityLiveRegion']
  isAtTopOfPage?: boolean
  isProviderPreview?: boolean
}

/**
 * A header component that sits above activity pages and displays metadata about
 * the assignment/response
 */

const STATUS_LABEL_MESSAGES = {
  [AssignmentStatuses.draft]: defineMessage({
    defaultMessage: 'Draft saved',
    description: 'Status label for a draft activity header',
  }),
  [AssignmentStatuses.completed]: defineMessage({
    defaultMessage: 'Submitted',
    description: 'Status label for a submitted activity header',
  }),
} as const

/**
 * putting all custom styles on the bottom of the defined styled components override the predefined style
 */

const HeaderContainer = styled(SafeAreaView)<{
  backgroundColor: string
  theme: ThemeType
  hasTopBorder?: boolean
}>(
  ({
    hasTopBorder,
    backgroundColor,
    theme: {
      spacing,
      breakpoints: { isMinWidthTablet },
      colors,
    },
  }) => ({
    backgroundColor,
    padding: isMinWidthTablet ? `${spacing['16px']} ${spacing['24px']}` : `${spacing['12px']} ${spacing['16px']}`,
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1,
    ...(hasTopBorder && {
      borderBottomColor: colors.borderDefault,
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
    }),
  }),
)

const Description = styled(View)<{ theme: ThemeType }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthTablet },
    },
  }) => ({
    justifyContent: 'center',
    alignItems: 'center',
    ...(!isMinWidthTablet && {
      paddingRight: spacing['24px'],
      paddingLeft: spacing['24px'],
    }),
  }),
)

const TitleContainer = styled.View({
  width: '100%',
  display: 'flex',
})

const CloseButton = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  position: 'absolute',
  right: '0px',
  top: spacing['4px'],
}))

const BackButton = styled(PressableOpacity)({
  position: 'absolute',
  left: '0px',
  top: '1px',
})

const TitleLabel = styled(Title)<{ theme: ThemeType; inReviewOrSubmittedMode: boolean }>(
  ({ theme: { spacing }, inReviewOrSubmittedMode }) => ({
    paddingBottom: inReviewOrSubmittedMode ? spacing['8px'] : spacing['4px'],
  }),
)

const DisplayContainer = styled.View<{ theme: ThemeType; isVisible: boolean; inReviewOrSubmittedMode: boolean }>(
  ({ isVisible, inReviewOrSubmittedMode, theme: { spacing, colors } }) => ({
    opacity: isVisible ? 1 : 0,
    ...(inReviewOrSubmittedMode && {
      padding: `${spacing['4px']} ${spacing['16px']}`,
      borderRadius: '20px',
      borderColor: colors.components.activityHeader.headline.border,
      borderStyle: 'solid',
      borderWidth: '1px',
      alignSelf: 'center',
      marginBottom: '0px',
      backgroundColor: colors.backgroundHighlightPrimary,
    }),
  }),
)

const ProgressBarComponent = styled(ProgressBar)({
  marginTop: '19px',
  marginBottom: '3px',
})

export const ActivityHeader: FunctionComponent<ActivityHeaderProps> = ({
  title = '',
  onBackPress,
  onClosePress,
  percentProgress,
  status,
  date,
  inReviewMode = false,
  backgroundColor,
  setHeaderHeight,
  a11yFocusedElementOnLoad,
  progressBarAccessibilityLiveRegion,
  isAtTopOfPage = false,
  isProviderPreview = false,
}) => {
  const [width, setWidth] = useState<number>(Layout.window.width)
  const { formatTime, formatMessage } = useIntl()
  const statusLabel =
    status && Object.prototype.hasOwnProperty.call(STATUS_LABEL_MESSAGES, status)
      ? formatMessage(STATUS_LABEL_MESSAGES[status as keyof typeof STATUS_LABEL_MESSAGES])
      : ''
  const isSubmitted = status === AssignmentStatuses.completed
  const statusAndDateLabel = `${statusLabel} ${formatTime(date !== null ? date : new Date())}`
  const {
    breakpoints: { isMinWidthTablet },
    colors,
    isAuroraTheme,
  } = useTheme()
  const inReviewOrSubmittedMode = inReviewMode || isSubmitted

  return (
    <HeaderContainer
      edges={['top']}
      testID={tID('ActivityHeader')}
      backgroundColor={getAuroraAwareBackgroundColor(isAuroraTheme, () =>
        isAtTopOfPage ? colors.backgroundSecondary : backgroundColor || colors.backgroundPrimary,
      )}
      hasTopBorder={!isAtTopOfPage}
      onLayout={(e: LayoutChangeEvent) => {
        setWidth(e.nativeEvent.layout.width)
        if (typeof setHeaderHeight === 'function') setHeaderHeight(e.nativeEvent.layout.height)
      }}
    >
      <TitleContainer>
        <Description>
          <View
            accessible
            accessibilityRole={!IS_WEB ? AccessibilityRolesNative.HEADER : undefined}
            ref={a11yFocusedElementOnLoad}
          >
            <TitleLabel
              level='1'
              size={TitleSize.XSMALL}
              color={colors.textPrimary}
              textAlign='center'
              text={title}
              inReviewOrSubmittedMode={inReviewOrSubmittedMode}
              noRole={!IS_WEB}
            />
            {!isProviderPreview && (
              <DisplayContainer
                isVisible={(Boolean(status) && Boolean(date)) || inReviewMode}
                inReviewOrSubmittedMode={inReviewOrSubmittedMode}
              >
                <Display
                  testID={tID(inReviewOrSubmittedMode ? 'ActivityHeader-Mode' : 'ActivityHeader-Display')}
                  size={DisplaySize.EYEBROW}
                  color={inReviewOrSubmittedMode ? colors.textActive : colors.textSecondary}
                  // formatTime does not accept param that can be a null type, so we must fallback to an acceptable type
                  // addtionally, since this component is conditionally rendered when date is not null, incorrect dates will not be shown
                  text={
                    inReviewOrSubmittedMode
                      ? isSubmitted
                        ? statusAndDateLabel
                        : formatMessage({
                            defaultMessage: 'Review',
                            description: 'Label for review mode',
                          })
                      : statusAndDateLabel
                  }
                  textAlign='center'
                  accessibilityRole={IS_WEB ? AccessibilityRolesNative.TEXT : undefined}
                  // Needed to resolve a11y issue - can't have an aria level without a header role
                  {...(inReviewOrSubmittedMode ? { level: '2' } : { level: null })}
                />
              </DisplayContainer>
            )}
          </View>
        </Description>
        {!!onBackPress && (
          <BackButton
            onPress={onBackPress}
            hitSlop={18}
            testID={tID('ActivityHeader-backButton')}
            accessibilityLabel={formatMessage(backAccessibilityLabel)}
          >
            <ArrowIcon size={26} fillColor={colors.iconDefault} direction={ArrowIconDirection.LEFT} />
          </BackButton>
        )}
        {!!onClosePress && (
          <CloseButton
            onPress={onClosePress}
            accessibilityRole='button'
            accessibilityLabel={formatMessage({
              defaultMessage: 'Close',
              description: 'button text Close',
            })}
            hitSlop={24}
            testID={tID('ActivityHeader-closeButton')}
            accessible
          >
            <CloseIcon size={13} fillColor={colors.iconDefault} />
          </CloseButton>
        )}
        {!isNil(percentProgress) && (
          <ProgressBarComponent
            color={colors.progressIndicator}
            progress={percentProgress}
            width={width - (isMinWidthTablet ? 48 : 32)}
            backgroundColor={colors.progressIndicatorBackgroundTop}
            height={4}
            borderRadius={100}
            duration={300}
            accessibilityLiveRegion={progressBarAccessibilityLiveRegion}
            showStopIndicator
          />
        )}
      </TitleContainer>
    </HeaderContainer>
  )
}
